import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const WalletOverview = ({ walletData, onTransferToTrading }) => {
  const [conversionAmount, setConversionAmount] = useState('');
  const [fromCurrency, setFromCurrency] = useState('USD');
  const [toCurrency, setToCurrency] = useState('BTC');

  const exchangeRates = {
    'BTC/USD': 27500.00,
    'ETH/USD': 1850.00,
    'USDT/USD': 1.00,
    'USD/BTC': 0.0000364,
    'USD/ETH': 0.000541
  };

  const calculateConversion = () => {
    if (!conversionAmount) return 0;
    const rate = exchangeRates[`${fromCurrency}/${toCurrency}`] || 
                 (1 / exchangeRates[`${toCurrency}/${fromCurrency}`]) || 1;
    return (parseFloat(conversionAmount) * rate).toFixed(8);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Total Balance Card */}
      <div className="lg:col-span-2 bg-surface rounded-trading border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-heading-semibold text-text-primary">
            Portfolio Balance
          </h2>
          <div className="flex items-center space-x-2">
            <Icon name="TrendingUp" size={16} className="text-success" />
            <span className="text-success font-body-medium text-sm">
              +{walletData.totalBalanceChange}%
            </span>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <p className="text-text-secondary font-caption-normal text-sm mb-1">
              Total Balance (USD)
            </p>
            <p className="text-3xl font-heading-bold text-text-primary">
              ${walletData.totalBalance.toLocaleString('en-US', { 
                minimumFractionDigits: 2, 
                maximumFractionDigits: 2 
              })}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-secondary-50 rounded-trading">
              <p className="text-text-secondary font-caption-normal text-sm mb-1">
                Available Balance
              </p>
              <p className="text-lg font-heading-semibold text-text-primary">
                ${walletData.availableBalance.toLocaleString('en-US', { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 2 
                })}
              </p>
            </div>
            <div className="p-4 bg-warning-50 rounded-trading">
              <p className="text-text-secondary font-caption-normal text-sm mb-1">
                Pending Balance
              </p>
              <p className="text-lg font-heading-semibold text-warning-700">
                ${walletData.pendingBalance.toLocaleString('en-US', { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 2 
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-border">
          <button
            onClick={() => onTransferToTrading('USD', 1000)}
            className="flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-trading font-body-medium hover:bg-primary-600 transition-colors duration-trading-fast"
          >
            <Icon name="ArrowRightLeft" size={16} />
            <span>Transfer to Trading</span>
          </button>
          <button className="flex items-center space-x-2 bg-secondary-100 text-text-primary px-4 py-2 rounded-trading font-body-medium hover:bg-secondary-200 transition-colors duration-trading-fast">
            <Icon name="Download" size={16} />
            <span>Export Statement</span>
          </button>
        </div>
      </div>

      {/* Quick Converter */}
      <div className="bg-surface rounded-trading border border-border p-6">
        <h3 className="text-lg font-heading-semibold text-text-primary mb-4">
          Quick Converter
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-body-medium text-text-secondary mb-2">
              From
            </label>
            <div className="flex space-x-2">
              <input
                type="number"
                value={conversionAmount}
                onChange={(e) => setConversionAmount(e.target.value)}
                placeholder="0.00"
                className="flex-1 px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <select
                value={fromCurrency}
                onChange={(e) => setFromCurrency(e.target.value)}
                className="px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="USD">USD</option>
                <option value="BTC">BTC</option>
                <option value="ETH">ETH</option>
                <option value="USDT">USDT</option>
              </select>
            </div>
          </div>

          <div className="flex justify-center">
            <button
              onClick={() => {
                const temp = fromCurrency;
                setFromCurrency(toCurrency);
                setToCurrency(temp);
              }}
              className="p-2 rounded-full hover:bg-secondary-50 transition-colors duration-trading-fast"
            >
              <Icon name="ArrowUpDown" size={16} className="text-text-secondary" />
            </button>
          </div>

          <div>
            <label className="block text-sm font-body-medium text-text-secondary mb-2">
              To
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={calculateConversion()}
                readOnly
                className="flex-1 px-3 py-2 border border-border rounded-trading bg-secondary-50 text-text-secondary"
              />
              <select
                value={toCurrency}
                onChange={(e) => setToCurrency(e.target.value)}
                className="px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="BTC">BTC</option>
                <option value="ETH">ETH</option>
                <option value="USD">USD</option>
                <option value="USDT">USDT</option>
              </select>
            </div>
          </div>

          <div className="pt-4 border-t border-border">
            <h4 className="text-sm font-body-medium text-text-secondary mb-2">
              Current Exchange Rates
            </h4>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary">BTC/USD</span>
                <span className="text-text-primary font-data">
                  ${exchangeRates['BTC/USD'].toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary">ETH/USD</span>
                <span className="text-text-primary font-data">
                  ${exchangeRates['ETH/USD'].toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletOverview;